import * as THREE from 'three';
import { ModelManager, CharacterModel } from '../ModelManager';

type Zone = 'head' | 'body' | 'legs';

export type AnimationState = 'idle' | 'attack' | 'block' | 'hit' | 'dodge' | 'death' | 'victory' | 'ability';

interface AnimationConfig {
  loop: boolean;
}

export class ModelAnimationController {
  private characterModel: CharacterModel | null = null;
  private currentState: AnimationState = 'idle';
  private currentAction: THREE.AnimationAction | null = null;
  private onCompleteCallback: (() => void) | null = null;
  private onDamagePointCallback: (() => void) | null = null;
  private damagePointTriggered: boolean = false;
  private isReady: boolean = false;
  private currentZone: Zone | null = null;
  private animationConfigs: Record<AnimationState, AnimationConfig> = {
    idle: { loop: true },
    attack: { loop: false },
    block: { loop: false },
    hit: { loop: false },
    dodge: { loop: false },
    death: { loop: false },
    victory: { loop: false }, // Should not loop, should transition to idle
    ability: { loop: false }
  };
  private eventFrames: Record<string, Record<string, number>> | null = null;

  constructor(
    private characterId: string,
    private modelManager: ModelManager,
    private scene: THREE.Scene,
    private initialPosition: THREE.Vector3,
    private initialRotation: THREE.Euler
  ) {
    this.setupMixerListener();
  }

  private setupMixerListener() {
    // This listener is set up once and handles all animation completions.
    const onFinished = (e: any) => {
      if (e.action === this.currentAction) {
        if (this.onCompleteCallback) {
          console.log(`Calling onCompleteCallback for ${this.currentState} animation`);
          const callback = this.onCompleteCallback;
          this.onCompleteCallback = null; // Clear callback immediately
          callback();
        }
        
        // Return to idle unless it's death
        if (this.currentState !== 'death') {
          console.log('Returning to idle after finished event');
          this.setState('idle');
        }
      }
    };

    // We need to get the mixer instance to add a listener.
    // This will be done after the model is loaded.
  }

  async initialize(
    scale: number = 0.01,
    modelPath?: string,
    texturePaths?: { diffuse?: string; weapon?: string },
    animationMapping?: any,
    baseUrl?: string,
    eventFrames?: Record<string, Record<string, number>>
  ) {
    // For now, use placeholder paths - in real implementation these would come from Supabase
    const model = modelPath || '';
    const textures = texturePaths || {};
    
    // Store event frames if provided
    if (eventFrames) {
      this.eventFrames = eventFrames;
    }

    try {
      this.characterModel = await this.modelManager.loadCharacterModel(
        this.characterId,
        model,
        textures,
        animationMapping,
        baseUrl
      );

      // Add model to scene
      this.scene.add(this.characterModel.model);

      // Нормализация по высоте как в админке
      const box = new THREE.Box3().setFromObject(this.characterModel.model);
      const size = box.getSize(new THREE.Vector3());
      
      // Базовое масштабирование по высоте (нормализуем к 1.8 единиц)
      const baseScale = 1.8 / size.y;
      const finalScale = baseScale * (scale || 0.01);
      this.characterModel.model.scale.set(finalScale, finalScale, finalScale);

      // Apply initial position and rotation после масштабирования
      this.characterModel.model.position.copy(this.initialPosition);
      this.characterModel.model.rotation.copy(this.initialRotation);

      // Add the persistent listener now that we have the mixer
      this.characterModel.mixer.addEventListener('finished', (e: any) => {
        if (e.action === this.currentAction) {
          if (this.onCompleteCallback) {
            const callback = this.onCompleteCallback;
            this.onCompleteCallback = null;
            callback();
          }
          if (this.currentState !== 'death') {
            this.setState('idle');
          }
        }
      });

      // Start with idle animation
      this.isReady = true;
      this.setState('idle');

      console.log(`Character ${this.characterId} initialized successfully with scale ${scale}`);
    } catch (error) {
      console.error(`Failed to initialize character ${this.characterId}:`, error);
      // Create a placeholder cube if model loading fails
      this.createPlaceholderModel(scale);
    }
  }

  private createPlaceholderModel(scale: number) {
    // Create a simple cube as placeholder
    const geometry = new THREE.BoxGeometry(1, 2, 0.5);
    const material = new THREE.MeshStandardMaterial({ 
      color: this.characterId === 'player' ? 0x00ff00 : 0xff0000 
    });
    const cube = new THREE.Mesh(geometry, material);
    cube.castShadow = true;
    cube.receiveShadow = true;

    const model = new THREE.Group();
    model.add(cube);
    model.position.copy(this.initialPosition);
    model.rotation.copy(this.initialRotation);
    model.scale.set(scale * 100, scale * 100, scale * 100);

    this.scene.add(model);

    // Create a dummy mixer and clips
    const mixer = new THREE.AnimationMixer(model);
    const createDummyClip = (name: string) => {
      const times = [0, 1];
      const values = [0, 0, 0, 0, 0.1, 0];
      const positionKF = new THREE.VectorKeyframeTrack('.position', times, values);
      return new THREE.AnimationClip(name, 1, [positionKF]);
    };

    this.characterModel = {
      model,
      mixer,
      clips: {
        idle: createDummyClip('idle'),
        attack: {
          head: createDummyClip('attack_head'),
          body: createDummyClip('attack_body'),
          legs: createDummyClip('attack_legs')
        },
        block: createDummyClip('block'),
        hit: createDummyClip('hit'),
        dodge: createDummyClip('dodge'),
        death: createDummyClip('death'),
        victory: createDummyClip('victory'),
        ability: createDummyClip('ability')
      }
    };
  }

  setState(
    state: AnimationState, 
    zone?: Zone, 
    onComplete?: () => void,
    onStart?: () => void
  ) {
    if (!this.characterModel || !this.isReady) {
      if (onComplete) onComplete();
      return;
    }

    // If trying to set the same state, ignore, unless it's a non-looping animation that can be retriggered.
    if (this.currentState === state && this.animationConfigs[state].loop && this.currentAction && this.currentAction.isRunning()) {
      return;
    }

    // Prevent state changes while dying, but allow the death animation itself.
    if (this.currentState === 'death' && state !== 'death') {
      if (onComplete) onComplete();
      return;
    }

    // If a non-looping animation is in progress and we get a new command,
    // we must resolve the old one before starting the new one.
    if (this.onCompleteCallback) {
      const oldCallback = this.onCompleteCallback;
      this.onCompleteCallback = null;
      oldCallback();
    }

    this.currentState = state;
    this.onCompleteCallback = onComplete || null;
    this.damagePointTriggered = false;

    // Get the appropriate animation clip
    let clip: THREE.AnimationClip | undefined;
    
    if (state === 'attack' && zone) {
      // Use zone-specific attack animation
      clip = this.characterModel.clips.attack[zone];
      this.currentZone = zone;
    } else {
      // Use standard animation
      clip = this.characterModel.clips[state as keyof typeof this.characterModel.clips] as THREE.AnimationClip;
      this.currentZone = null;
    }

    if (!clip) {
      console.error(`No animation clip found for state: ${state} for character ${this.characterId}`);
      // If idle animation is missing, we might be in a T-pose. Let's try to recover.
      if (state === 'idle' && this.characterModel.clips.idle) {
        clip = this.characterModel.clips.idle;
        console.warn(`Falling back to default idle animation for ${this.characterId}`);
      } else {
        if (onComplete) {
          onComplete();
        }
        return;
      }
    }

    // Create and play new animation
    const newAction = this.characterModel.mixer.clipAction(clip);
    newAction.reset();
    
    // Configure animation settings
    const config = this.animationConfigs[state];
    newAction.setLoop(config.loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    newAction.clampWhenFinished = !config.loop;
    
    if (this.currentAction) {
      this.currentAction.fadeOut(0.2);
    }
    
    newAction.fadeIn(0.2).play();
    this.currentAction = newAction;

    // Call onStart immediately
    if (onStart) {
      onStart();
    }

    // If the new animation is looping, we must clear any pending onComplete callback.
    if (config.loop && this.onCompleteCallback) {
      this.onCompleteCallback = null;
    }
  }

  update(deltaTime: number) {
    if (!this.characterModel) return;

    // Update animation mixer
    const deltaSeconds = deltaTime / 1000;
    this.characterModel.mixer.update(deltaSeconds);

    // Check for damage point callback
    if (this.currentState === 'hit' && this.onDamagePointCallback && !this.damagePointTriggered) {
      // For simplicity, trigger damage point callback halfway through the hit animation
      if (this.currentAction && this.currentAction.time >= this.currentAction.getClip().duration / 2) {
        this.onDamagePointCallback();
        this.damagePointTriggered = true;
      }
    }
  }

  getCurrentState(): AnimationState {
    return this.currentState;
  }

  getCurrentFrame(): number {
    if (!this.currentAction || !this.currentAction.getClip()) {
      return 0;
    }
    
    // Get current time in the animation
    const currentTime = this.currentAction.time;
    
    // Convert time to frame (assuming 30 FPS)
    const fps = 30;
    const currentFrame = Math.floor(currentTime * fps);
    
    return currentFrame;
  }

  getAttackEventFrames(zone: 'head' | 'body' | 'legs'): { hit: number; block: number; dodge: number; death: number } | undefined {
    if (!this.eventFrames || !this.eventFrames[zone]) {
      return undefined;
    }
    
    return this.eventFrames[zone] as { hit: number; block: number; dodge: number; death: number };
  }

  getAbilityEventFrames(): { hit: number; death: number } | undefined {
    if (!this.eventFrames || !this.eventFrames.ability) {
      return undefined;
    }
    
    return this.eventFrames.ability as { hit: number; death: number };
  }

  getPosition(): THREE.Vector3 {
    if (!this.characterModel) return this.initialPosition;
    return this.characterModel.model.position;
  }

  setPosition(position: THREE.Vector3) {
    if (this.characterModel) {
      this.characterModel.model.position.copy(position);
    }
    this.initialPosition.copy(position);
  }

  setRotation(rotation: THREE.Euler) {
    if (this.characterModel) {
      this.characterModel.model.rotation.copy(rotation);
    }
    this.initialRotation.copy(rotation);
  }

  reset() {
    // Reset state to idle (used for battle restart)
    this.currentState = 'idle';
    this.setState('idle');
  }

  dispose() {
    if (this.currentAction) {
      this.currentAction.stop();
    }
    
    if (this.characterModel) {
      this.characterModel.mixer.stopAllAction();
      
      if (this.characterModel.model.parent) {
        this.characterModel.model.parent.remove(this.characterModel.model);
      }
    }
  }
}
